/**
 * ConfigService - Gestione configurazione runtime
 * Carica la configurazione da un file JSON pubblico che può essere modificato senza ricompilare
 */

export interface FeatureFlags {
  showPrenotazioni: boolean;
  showAppuntamenti: boolean;
  showRefertiOnline: boolean;
  showRegistrazione: boolean;
}

export interface AppConfig {
  api: {
    baseUrl: string;
    // NOTA: clientId e clientSecret NON devono essere nel config pubblico
    // Devono rimanere nel .env o essere gestiti lato server
  };
  branding?: {
    title: string;
    description: string;
    name: string;
    headerTitle?: string;
    headerSubtitle?: string;
  };
  theme?: {
    primaryColor: string;
    primaryColorHover?: string;
  };
  support?: {
    email: string;
  };
  logo: {
    url: string;
    width: number;
    height: number;
    alt: string;
    mainSiteUrl: string;
  };
  address1: {
    label: string;
    address: string;
    url: string;
    telephone: string;
  };
  address2: {
    show: boolean;
    label: string;
    address: string;
    url: string;
    showTelephone: boolean;
    telephone: string;
  };
  social: {
    showFacebook: boolean;
    facebookUrl: string;
    facebookText: string;
  };
  footer: {
    line1: string;
    line2: string;
    line2LinkText: string;
    line2LinkUrl: string;
    line3: string;
  };
  reports?: {
    reportsCompanyFilter?: string;
  };
  featureFlags?: FeatureFlags;
  messages?: {
    noActivatedUserMessage?: string;
  };
}

class ConfigService {
  private static instance: ConfigService;
  private config: AppConfig | null = null;
  private loadingPromise: Promise<AppConfig> | null = null;

  private constructor() {}

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  /**
   * Carica la configurazione dal file JSON
   * Usa caching per evitare richieste multiple
   */
  async loadConfig(): Promise<AppConfig> {
    // Se la configurazione è già caricata, ritornala
    if (this.config) {
      return this.config;
    }

    // Se stiamo già caricando, ritorna la promise esistente
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Inizia il caricamento
    this.loadingPromise = this.fetchConfig();

    try {
      this.config = await this.loadingPromise;
      return this.config;
    } finally {
      this.loadingPromise = null;
    }
  }

  /**
   * Fetch effettivo del file di configurazione
   */
  private async fetchConfig(): Promise<AppConfig> {
    try {
      // In produzione, il file sarà servito dalla stessa origin
      // In development, webpack dev server lo servirà dalla cartella public
      // Aggiungi timestamp per forzare il refresh (cache busting)
      const timestamp = new Date().getTime();
      const response = await fetch(`/config.json?v=${timestamp}`, {
        cache: "no-cache", // Evita problemi di cache del browser
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to load config: ${response.status} ${response.statusText}`
        );
      }

      const config = await response.json();

      // Validazione base della struttura
      this.validateConfig(config);

      console.log("✅ Configurazione caricata con successo:", config);
      return config;
    } catch (error) {
      console.error("❌ ERRORE CRITICO: Impossibile caricare configurazione:", error);
      console.error("❌ L'applicazione utilizzerà segnaposto per tutti i valori di configurazione");

      // Ritorna configurazione con segnaposto in caso di errore
      return this.getPlaceholderConfig();
    }
  }

  /**
   * Validazione della configurazione
   */
  private validateConfig(config: any): void {
    if (!config.api || !config.logo || !config.address1 || !config.footer) {
      throw new Error(
        "Configurazione non valida: mancano sezioni obbligatorie"
      );
    }
    if (!config.api.baseUrl) {
      throw new Error("Configurazione API non valida: manca baseUrl");
    }
    // support è opzionale per retrocompatibilità
  }

  /**
   * Configurazione con segnaposto (fallback quando config.json non è disponibile)
   */
  private getPlaceholderConfig(): AppConfig {
    console.warn("⚠️ ATTENZIONE: Usando configurazione con segnaposto - config.json non disponibile!");
    return {
      api: {
        baseUrl: "/",
      },
      support: {
        email: "[CONFIGURAZIONE NON DISPONIBILE]",
      },
      logo: {
        url: "",
        width: 200,
        height: 80,
        alt: "[Logo non disponibile]",
        mainSiteUrl: "#",
      },
      address1: {
        label: "[Configurazione non disponibile]",
        address: "[Configurazione non disponibile]",
        url: "#",
        telephone: "[N/A]",
      },
      address2: {
        show: false,
        label: "",
        address: "",
        url: "#",
        showTelephone: false,
        telephone: "",
      },
      social: {
        showFacebook: false,
        facebookUrl: "#",
        facebookText: "",
      },
      footer: {
        line1: "[Configurazione non disponibile]",
        line2: "",
        line2LinkText: "",
        line2LinkUrl: "#",
        line3: "",
      },
    };
  }

  /**
   * Ottiene la configurazione corrente (sincrono)
   * Ritorna null se non ancora caricata
   */
  getConfig(): AppConfig | null {
    return this.config;
  }

  /**
   * Forza il ricaricamento della configurazione
   */
  async reloadConfig(): Promise<AppConfig> {
    this.config = null;
    this.loadingPromise = null;
    return this.loadConfig();
  }

  /**
   * Ottiene la base URL dell'API dalla configurazione
   * Ritorna '/' come default se non configurato
   */
  getApiBaseUrl(): string {
    const config = this.getConfig();
    if (config?.api?.baseUrl) {
      return config.api.baseUrl;
    }
    // Fallback su URL relativo
    return "/";
  }

  /**
   * Costruisce un URL completo aggiungendo il path alla base URL
   * Usa questo per endpoint che hanno il prefisso /api/ nel backend
   */
  buildApiUrl(path: string): string {
    const baseUrl = this.getApiBaseUrl();
    // Rimuovi slash iniziale dal path se presente
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    // Assicura che baseUrl finisca con /
    const cleanBase = baseUrl.endsWith("/") ? baseUrl : baseUrl + "/";
    return cleanBase + cleanPath;
  }

  /**
   * Costruisce un URL senza il prefisso /api/
   * Usa questo per endpoint legacy che NON hanno /api/ nel backend
   * In development usa il proxy, in production usa l'origin corrente
   */
  buildUrl(path: string): string {
    // Per endpoint senza /api/, usa sempre window.location.origin
    const origin = window.location.origin;
    // Rimuovi slash iniziale dal path se presente
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    return `${origin}/${cleanPath}`;
  }

  /**
   * Ottiene il titolo della pagina dalla configurazione
   */
  getPageTitle(): string {
    return this.config?.branding?.title || "Portale Paziente";
  }

  /**
   * Ottiene la description dalla configurazione
   */
  getPageDescription(): string {
    return this.config?.branding?.description || "Portale paziente - Referti online";
  }

  /**
   * Ottiene il nome del brand dalla configurazione
   */
  getBrandName(): string {
    return this.config?.branding?.name || "Portale Paziente";
  }

  /**
   * Ottiene il titolo dell'header dalla configurazione
   */
  getHeaderTitle(): string {
    return this.config?.branding?.headerTitle || "Referti on-line";
  }

  /**
   * Ottiene il sottotitolo dell'header dalla configurazione
   */
  getHeaderSubtitle(): string {
    return this.config?.branding?.headerSubtitle || "";
  }

  /**
   * Ottiene il colore primario dalla configurazione
   */
  getPrimaryColor(): string {
    return this.config?.theme?.primaryColor || "#229a61";
  }

  /**
   * Ottiene il colore primario hover dalla configurazione
   */
  getPrimaryColorHover(): string {
    return this.config?.theme?.primaryColorHover || this.getPrimaryColor();
  }

  /**
   * Ottiene il messaggio per utenti non ancora attivati
   */
  getNoActivatedUserMessage(): string {
    return this.config?.messages?.noActivatedUserMessage ||
      "Il tuo account non è ancora stato attivato. Contatta l'assistenza per maggiori informazioni.";
  }
}

export default ConfigService.getInstance();
